/**
 * MyBirthdayPage.jsx — v1.0.0 Gate 1
 * Birthday countdown, saved offers, itinerary access, wanted promos.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useUserState } from "@/contexts/UserStateContext";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { Gift, Calendar, Heart, Clock, ChevronRight, Bookmark, Loader2 } from "lucide-react";

export default function MyBirthdayPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { state, days_until_birthday, greeting } = useUserState();
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    axios.get(`${API}/favorites`).then((r) => setSaved(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const countdownLabel = () => {
    if (state === "birthday_day") return "Today is your birthday!";
    if (state === "post_birthday_7") return "Hope you had a great birthday!";
    if (days_until_birthday === 1) return "Tomorrow is your birthday!";
    if (days_until_birthday != null) return `${days_until_birthday} days until your birthday`;
    return "Set your birthday to get started";
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="my-birthday-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: theme.headerGradient }}>
        <h1 className="text-xl font-bold text-white" data-testid="my-birthday-title">My Birthday</h1>
        <p className="text-white/70 text-sm mt-1" data-testid="my-birthday-countdown-label">{countdownLabel()}</p>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Countdown card */}
        {days_until_birthday != null && state !== "birthday_day" && state !== "post_birthday_7" && (
          <div
            className="rounded-2xl p-5 text-center"
            style={{ backgroundColor: theme.primaryBgAccent, border: `1px solid ${theme.primaryLight}` }}
            data-testid="birthday-countdown-card"
          >
            <p className="text-4xl font-black" style={{ color: theme.primary }}>{days_until_birthday}</p>
            <p className="text-sm text-slate-600 mt-1">days to go</p>
          </div>
        )}

        {/* Quick actions */}
        {[
          { label: "Saved Deals", icon: Heart, count: saved.length, path: "/my-deals", testId: "my-bday-saved" },
          { label: "My Itinerary", icon: Calendar, path: "/my-itinerary", testId: "my-bday-itinerary" },
          { label: "Wanted Promos", icon: Bookmark, path: "/wanted-promos", testId: "my-bday-wanted" },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 shadow-sm active:scale-[0.99] transition-transform"
            data-testid={item.testId}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.primaryBgAccent }}>
                <item.icon className="w-5 h-5" style={{ color: theme.primary }} />
              </div>
              <span className="text-sm font-semibold text-slate-800">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {item.count != null && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.primaryBgAccent, color: theme.primary }}>
                  {item.count}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          </button>
        ))}

        {/* Empty state */}
        {!loading && saved.length === 0 && (
          <div className="flex items-center justify-center min-h-[40vh]" data-testid="my-birthday-empty-state">
            <div className="text-center px-8">
              <p className="text-base font-semibold text-slate-700 leading-relaxed">
                Nothing saved yet.<br />
                Start collecting your birthday deals.
              </p>
              <button
                onClick={() => navigate("/discover")}
                className="mt-6 px-6 py-3 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: theme.buttonBg }}
                data-testid="my-birthday-start-planning-btn"
              >
                Browse Offers
              </button>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
